import { useApp } from '@/contexts';

import { useEffect, useState } from 'react';

import { Alert, Box, Button, Paper, TextField, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router';

import { ConfirmDialog, PageLoader } from '@/components/common';
import { PageContainer, PageHeading } from '@/components/layout';
import { useGetTenantQuery } from '@/features/tenants/api/tenantsApi';
import { logger } from '@/utils/logger';

import {
  useCreateObjectTypeMutation,
  useDeleteObjectTypeMutation,
  useGetAllObjectTypesQuery,
  useUpdateObjectTypeMutation,
} from '../api/eventsApi';

const nameRegex = /[^A-Za-z0-9_-]/;

export const EventsObjectFormPage = () => {
  const { appId, tenantId, app } = useApp();
  const navigate = useNavigate();
  const { objId } = useParams<{ objId?: string }>();
  const [objectName, setObjectName] = useState('mynewobject');
  const [privatePref] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { data: tenant } = useGetTenantQuery(tenantId, {
    skip: !tenantId,
  });

  const { data: objectTypes } = useGetAllObjectTypesQuery(
    {
      tenantKey: tenant?.key || '',
      appKey: app.uniqueKey || '',
    },
    { skip: !tenant?.key || !app.uniqueKey }
  );

  const [createObject, { isLoading: isCreating }] = useCreateObjectTypeMutation();
  const [updateObject, { isLoading: isUpdating }] = useUpdateObjectTypeMutation();
  const [deleteObject, { isLoading: isDeleting }] = useDeleteObjectTypeMutation();

  const isEditMode = !!objId;
  const isProcessing = isCreating || isUpdating || isDeleting;

  // Initialize object name for edit mode or generate for new
  useEffect(() => {
    if (objId && tenant?.key && app.uniqueKey) {
      // Extract object name from full path (tenantKey.appKey.objectName)
      const parts = objId.split('.');
      if (parts.length >= 3) {
        const name = parts.slice(2).join('.');
        setObjectName(name);
      }
    } else if (objectTypes && !objId) {
      // Generate next available name for new objects
      const privateObjects = objectTypes.filter((o) => o.private);
      const nextNumber = privateObjects.length + 1;
      setObjectName(`mynewobject${nextNumber}`);
    }
  }, [objId, objectTypes, tenant?.key, app.uniqueKey]);

  const getValidationState = (): 'error' | 'success' => {
    if (objectName !== '' && !nameRegex.test(objectName)) {
      return 'success';
    }
    return 'error';
  };

  const isNameInUse = (): boolean => {
    if (!objectTypes || !tenant?.key || !app.uniqueKey) return false;

    // Get the original name for edit mode
    let originalName = '';
    if (objId) {
      const parts = objId.split('.');
      if (parts.length >= 3) {
        originalName = parts.slice(2).join('.');
      }
    }

    return objectTypes.some((o) => {
      const parts = o.name.split('.');
      const existingName = parts.slice(2).join('.');
      return objectName === existingName && objectName !== originalName;
    });
  };

  const isFormValid = (): boolean => {
    return (
      getValidationState() === 'success' && !isNameInUse() && objectName !== '' && !isProcessing
    );
  };

  const handleCreate = async () => {
    if (!tenant?.key || !app.uniqueKey || !isFormValid()) return;

    const fullName = objId
      ? (() => {
          const parts = objId.split('.');
          return `${parts[0]}.${parts[1]}.${objectName}`;
        })()
      : `${tenant.key}.${app.uniqueKey}.${objectName}`;

    const data = {
      name: fullName,
      private: privatePref,
    };

    try {
      if (isEditMode && objId) {
        await updateObject({
          tenantKey: tenant.key,
          appKey: app.uniqueKey,
          name: objId,
          data,
        }).unwrap();
      } else {
        await createObject({
          tenantKey: tenant.key,
          appKey: app.uniqueKey,
          data,
        }).unwrap();
      }
      navigate(`/a/${appId}/events/obj`);
    } catch (error) {
      logger.error('Failed to save object:', error);
    }
  };

  const handleDelete = async () => {
    if (!tenant?.key || !app.uniqueKey || !objId) return;

    try {
      await deleteObject({
        tenantKey: tenant.key,
        appKey: app.uniqueKey,
        name: objId,
      }).unwrap();
      setDeleteDialogOpen(false);
      navigate(`/a/${appId}/events/obj`);
    } catch (error) {
      logger.error('Failed to delete object:', error);
    }
  };

  const handleCancel = () => {
    navigate(`/a/${appId}/events/obj`);
  };

  if (!tenant?.key || !app.uniqueKey) {
    return (
      <PageContainer>
        <PageLoader />
      </PageContainer>
    );
  }

  const tenantKey = tenant.key;
  const appKey = app.uniqueKey;
  const prefix =
    isEditMode && objId ? objId.split('.').slice(0, 2).join('.') : `${tenantKey}.${appKey}`;

  const hasNameError = nameRegex.test(objectName);
  const nameInUse = isNameInUse();

  return (
    <PageContainer sx={{ height: 'auto' }}>
      <PageHeading
        title={isEditMode ? `Edit "${objectName}"` : 'New Object'}
        subtitle={
          isEditMode
            ? 'Modify event object type configuration'
            : 'Create a new event object type for REST API events'
        }
      />

      <Box>
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, fontSize: '11px', display: 'block', mb: 0.5 }}
        >
          Object Name
        </Typography>

        <TextField
          fullWidth
          size="small"
          placeholder="Object Name"
          value={objectName}
          onChange={(e) => setObjectName(e.target.value)}
          error={hasNameError || nameInUse}
          slotProps={{
            input: {
              startAdornment: (
                <Typography
                  component="span"
                  sx={{
                    mr: 0.5,
                    color: 'text.secondary',
                    fontSize: '14px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {prefix}.
                </Typography>
              ),
            },
          }}
          sx={{ mb: 1 }}
        />

        {hasNameError && (
          <Alert severity="error" sx={{ mb: 2, py: 0, fontSize: '11px' }}>
            Alphabetic and numeric characters allowed.
          </Alert>
        )}

        {nameInUse && (
          <Alert severity="error" sx={{ mb: 2, py: 0, fontSize: '11px' }}>
            This name is already in use.
          </Alert>
        )}

        <Typography
          variant="caption"
          sx={{ fontWeight: 600, fontSize: '11px', display: 'block', mb: 0.5, mt: 2 }}
        >
          Event Listener
        </Typography>

        <Typography variant="body2" sx={{ fontSize: '14px', mb: 3 }}>
          https://www.lingkapis.com/v1/{tenantKey}/{appKey}/events
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mb: 4 }}>
          {isEditMode && (
            <Button
              variant="contained"
              color="error"
              size="small"
              loading={isDeleting}
              onClick={() => setDeleteDialogOpen(true)}
              sx={{ mr: 'auto' }}
            >
              Delete Object
            </Button>
          )}
          <Button
            variant="outlined"
            color="error"
            size="small"
            disabled={isProcessing}
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            disabled={!isFormValid()}
            loading={isCreating || isUpdating}
            onClick={handleCreate}
          >
            {isEditMode ? 'Save' : 'Create Object'}
          </Button>
        </Box>

        <Typography variant="h6" sx={{ mb: 2, fontWeight: 400 }}>
          Example Payload
        </Typography>

        <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
          <pre
            style={{
              margin: 0,
              fontFamily: 'monospace',
              fontSize: '13px',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {`"eventObject": {
  "course": {
    "courseCode": "24617.1101"
  }
},
"objectType": "${tenantKey}.${appKey}.${objectName}",
"verb": "finish"`}
          </pre>
        </Paper>
      </Box>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title="Delete Object"
        message={
          <>
            Are you sure you want to delete <strong>"{objectName}"</strong>? This action cannot be
            undone and will affect all event subscriptions using this object type.
          </>
        }
        confirmText="Delete Object"
        confirmColor="error"
        isLoading={isDeleting}
        loadingText="Deleting..."
      />
    </PageContainer>
  );
};
